import { getLoginContext } from '@athena-lib/loginContext.svelte';
import { pushEvent } from './setup';
import {
  GtmEventNames,
  type GtmEcommerceEvent,
  type EcommerceItem,
  type GtmEventWithProps,
  type TrackAnalytics,
  type Tracking,
  type SearchEvent,
  type CTAEvent,
  type ChatEvent,
  type FormEvent,
  type QuoteFormEvent,
} from './types';

export function trackStartEvent() {
  pushEvent<GtmEventWithProps>({
    event: GtmEventNames.Start,
    'gtm.start': new Date().getTime(),
  });
}

export function trackItemViewList(items: () => EcommerceItem[]) {
  pushEvent<GtmEcommerceEvent>({
    event: GtmEventNames.ViewItemList,
    ecommerce: {
      items: items(),
    },
  });
}

export function trackItemView(item: EcommerceItem) {
  pushEvent<GtmEcommerceEvent>({
    event: GtmEventNames.ViewItem,
    ecommerce: {
      items: [item],
    },
  });
}

export function trackItemSelect(item: EcommerceItem) {
  pushEvent<GtmEcommerceEvent>({
    event: GtmEventNames.SelectItem,
    ecommerce: {
      items: [item],
    },
  });
}

export function trackAddToCart(item: EcommerceItem) {
  pushEvent<GtmEcommerceEvent>({
    event: GtmEventNames.AddToCart,
    ecommerce: {
      items: [item],
    },
  });
}

export function trackAnalyticsEvent(analytics: TrackAnalytics) {
  pushEvent<GtmEventWithProps>({
    event: GtmEventNames.AnalyticsEvent,
    'event.category': analytics.eventCategory,
    'event.action': analytics.eventAction,
    'event.label': analytics.eventLabel,
    'event.value': analytics.eventValue ?? 0,
  });
}

export function trackQuoteForm(
  action: string,
  slug: string,
  variant: 'stickerpack_quote_form' | 'static_quote_form' | 'multiple_quote_form',
  error_message?: string,
  error_type?: string,
  order_id?: string,
) {
  const event: QuoteFormEvent = {
    event: GtmEventNames.FormInteraction,
    event_params: {
      event_action: action,
      event_variant: variant,
      default_path: slug,
      event_group: 'quote_form',
      page_type: 'quote_page',
      signed_in: getLoginContext().isLoggedIn,
    },
  };
  if (error_message) {
    event.event_params.error_message = error_message;
  }
  if (error_type) {
    event.event_params.error_type = error_type;
  }
  if (order_id) {
    event.event_params.quote_order_id = order_id;
  }
  pushEvent<QuoteFormEvent>(event);
}

export function trackProductWizard(event: Tracking) {
  pushEvent<Tracking>(event);
}

export function trackSearch(event: SearchEvent) {
  pushEvent<SearchEvent>(event);
}

export function trackCTAButton(event: CTAEvent) {
  pushEvent<CTAEvent>(event);
}

export function trackChat(event: ChatEvent) {
  pushEvent<ChatEvent>(event);
}

export function trackForm(event: FormEvent) {
  pushEvent<FormEvent>(event);
}

export function trackReorderForm(action: string) {
  pushEvent<Tracking>({
    event: GtmEventNames.FormInteraction,
    event_params: {
      event_action: action,
      event_group: 'reorder_form',
      event_variant: 'reorder_page',
      page_type: 'reorder_page',
      signed_in: getLoginContext().isLoggedIn,
    },
  });
}
export function trackKlaviyoForm(action: string, variant: string, slug?: string) {
  pushEvent<Tracking>({
    event: GtmEventNames.FormInteraction,
    event_params: {
      event_action: action,
      event_variant: variant,
      page_type: slug ?? '',
      event_group: 'klaviyo_form',
      signed_in: getLoginContext().isLoggedIn,
    },
  });
}
